import { FormEvent, useEffect, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowLeft,
  CornerUpLeft,
  DownloadCloud,
  Edit3,
  FileText,
  Image as ImageIcon,
  Link2,
  Loader2,
  MessageCircle,
  MoreHorizontal,
  Paperclip,
  Send,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { Socket } from "socket.io-client";

import {
  ChatContext,
  ChatMessage,
  chatApi,
  createChatSocket,
} from "../../api/chat";
import { useAppSelector } from "../../hooks/reducer";

type ChatDrawerProps = {
  open: boolean;
  onClose: () => void;
  cohortId: string;
  cohortName?: string;
  recipientId?: string | null;
  recipientName?: string;
  embedded?: boolean;
  onMessageSent?: (message: ChatMessage) => void;
  onMobileBack?: () => void;
};

const authorOf = (message: ChatMessage) =>
  typeof message.authorId === "string"
    ? { _id: message.authorId, username: "Учасник" }
    : message.authorId || { _id: "", username: "Користувач" };

const authorName = (author?: { username?: string; email?: string } | null) =>
  author?.username?.trim() || author?.email?.trim() || "Користувач";

const replyOf = (message: ChatMessage) =>
  message.replyToId && typeof message.replyToId !== "string"
    ? message.replyToId
    : null;

const mergeMessage = (items: ChatMessage[], incoming: ChatMessage) => {
  const exists = items.some((item) => item._id === incoming._id);
  return exists
    ? items.map((item) => (item._id === incoming._id ? incoming : item))
    : [...items, incoming];
};

const CHAT_FILE_ACCEPT =
  ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.webp,.gif,.zip,.rar,.7z";
const MAX_CHAT_FILE_SIZE = 25 * 1024 * 1024;
const MAX_CHAT_ATTACHMENTS = 5;
const ALLOWED_CHAT_EXTENSIONS = new Set(
  CHAT_FILE_ACCEPT.split(",").map((item) => item.replace(".", ""))
);

const formatFileSize = (size = 0) => {
  if (size >= 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} МБ`;
  if (size >= 1024) return `${Math.round(size / 1024)} КБ`;
  return `${size} Б`;
};

const fileExtension = (name: string) =>
  name.split(".").pop()?.toLowerCase() || "";

const isImageAttachment = (mimeType?: string, name = "") =>
  mimeType?.startsWith("image/") ||
  ["jpg", "jpeg", "png", "webp", "gif"].includes(fileExtension(name));

const imagePreviewUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes("dropbox.com")) {
      parsed.searchParams.delete("dl");
      parsed.searchParams.set("raw", "1");
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

const ChatDrawer = ({
  open,
  onClose,
  cohortId,
  cohortName,
  recipientId = null,
  recipientName,
  embedded = false,
  onMessageSent,
  onMobileBack,
}: ChatDrawerProps) => {
  const currentUser = useAppSelector(
    (state) => state.authReducer.data?.user
  );
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showLink, setShowLink] = useState(false);
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const [editing, setEditing] = useState<ChatMessage | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sending, setSending] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState("");
  const [typingUsers, setTypingUsers] = useState<Record<string, string>>({});
  const [menuId, setMenuId] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [previewImage, setPreviewImage] = useState<{
    url: string;
    name: string;
  } | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragDepthRef = useRef(0);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const context: ChatContext = { cohortId, recipientId };

  const scrollToBottom = (behavior: ScrollBehavior = "smooth") =>
    window.setTimeout(
      () => bottomRef.current?.scrollIntoView({ behavior }),
      0
    );

  useEffect(() => {
    if (!open || !cohortId) return;
    let active = true;
    setLoading(true);
    setMessages([]);
    setError("");

    chatApi
      .listMessages({ cohortId, recipientId })
      .then((data) => {
        if (!active) return;
        setMessages(data.messages);
        setHasMore(data.hasMore);
        scrollToBottom("auto");
      })
      .catch((requestError: any) => {
        if (active) {
          setError(
            requestError.response?.data?.message ||
              "Не вдалося завантажити чат."
          );
        }
      })
      .finally(() => active && setLoading(false));

    const socket = createChatSocket();
    socketRef.current = socket;
    socket.emit("chat:join", { cohortId, recipientId });

    const belongsToCurrentChat = (message: ChatMessage) => {
      if (String(message.cohortId) !== String(cohortId)) return false;
      const messageRecipientId =
        message.recipientId && typeof message.recipientId !== "string"
          ? message.recipientId._id
          : message.recipientId;
      if (!recipientId) return !messageRecipientId;
      const authorId = authorOf(message)._id;
      return [String(authorId || ""), String(messageRecipientId || "")].includes(
        String(recipientId)
      );
    };

    const handleNew = (message: ChatMessage) => {
      if (!belongsToCurrentChat(message)) return;
      setMessages((current) => mergeMessage(current, message));
      scrollToBottom();
    };
    const handleChanged = (message: ChatMessage) => {
      if (!belongsToCurrentChat(message)) return;
      setMessages((current) => mergeMessage(current, message));
    };
    const handleTyping = (payload: any) => {
      if (
        String(payload.cohortId) !== String(cohortId) ||
        String(payload.recipientId || "") !== String(recipientId || "") ||
        String(payload.userId) === String(currentUser?._id)
      ) {
        return;
      }
      setTypingUsers((current) => {
        const next = { ...current };
        if (payload.isTyping) next[payload.userId] = payload.username || "Користувач";
        else delete next[payload.userId];
        return next;
      });
    };

    socket.on("chat:message:new", handleNew);
    socket.on("chat:message:updated", handleChanged);
    socket.on("chat:message:deleted", handleChanged);
    socket.on("chat:typing", handleTyping);
    socket.on("connect", () => {
      setError("");
      socket.emit("chat:join", { cohortId, recipientId });
    });
    socket.on("connect_error", (socketError) => {
      const message = socketError.message || "";
      setError(
        message === "Invalid or expired token"
          ? "Сесія завершилась. Увійдіть у кабінет повторно."
          : "Немає з’єднання з чатом. Перевірте, чи запущений оновлений бекенд."
      );
    });

    return () => {
      active = false;
      socket.emit("chat:leave", { cohortId, recipientId });
      socket.disconnect();
      socketRef.current = null;
      setTypingUsers({});
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    };
  }, [open, cohortId, recipientId, currentUser?._id]);

  const loadMore = async () => {
    if (!messages[0] || loadingMore) return;
    try {
      setLoadingMore(true);
      const data = await chatApi.listMessages(
        context,
        messages[0].createdAt
      );
      setMessages((current) => [...data.messages, ...current]);
      setHasMore(data.hasMore);
    } catch (requestError: any) {
      setError(
        requestError.response?.data?.message ||
          "Не вдалося завантажити попередні повідомлення."
      );
    } finally {
      setLoadingMore(false);
    }
  };

  const notifyTyping = (value: string) => {
    setText(value);
    socketRef.current?.emit("chat:typing", {
      ...context,
      isTyping: Boolean(value.trim()),
    });
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      socketRef.current?.emit("chat:typing", {
        ...context,
        isTyping: false,
      });
    }, 1200);
  };

  const handleFilesSelected = (files: FileList | null) => {
    if (!files?.length) return;

    const incoming = Array.from(files);
    const validFiles: File[] = [];

    for (const file of incoming) {
      const extension = fileExtension(file.name);
      if (!ALLOWED_CHAT_EXTENSIONS.has(extension)) {
        setError(
          "Можна надсилати PDF, Word, Excel, PowerPoint, TXT, фото та архіви."
        );
        continue;
      }

      if (file.size > MAX_CHAT_FILE_SIZE) {
        setError(`Файл "${file.name}" має бути до 25 МБ.`);
        continue;
      }

      validFiles.push(file);
    }

    if (!validFiles.length) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setSelectedFiles((current) => {
      const next = [...current, ...validFiles].slice(0, MAX_CHAT_ATTACHMENTS);
      if (current.length + validFiles.length > MAX_CHAT_ATTACHMENTS) {
        setError("В одному повідомленні можна надіслати до 5 файлів.");
      } else {
        setError("");
      }
      return next;
    });

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const hasDraggedFiles = (event: React.DragEvent) =>
    Array.from(event.dataTransfer.types || []).includes("Files");

  const handleDragEnter = (event: React.DragEvent) => {
    if (editing || !hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    setDragActive(true);
  };

  const handleDragOver = (event: React.DragEvent) => {
    if (editing || !hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "copy";
    setDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    if (editing || !hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragActive(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    if (editing) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setDragActive(false);
    handleFilesSelected(event.dataTransfer.files);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const cleanText = text.trim();
    const cleanLink = linkUrl.trim();
    if (!cleanText && !cleanLink && selectedFiles.length === 0) return;

    if (editing && selectedFiles.length > 0) {
      setError("Під час редагування можна змінювати тільки текст.");
      return;
    }

    if (selectedFiles.length + (cleanLink ? 1 : 0) > MAX_CHAT_ATTACHMENTS) {
      setError("В одному повідомленні можна надіслати до 5 вкладень.");
      return;
    }

    try {
      setSending(true);
      setError("");
      if (editing) {
        const updated = await chatApi.editMessage(editing._id, cleanText);
        setMessages((current) => mergeMessage(current, updated));
      } else {
        const uploadedAttachments = selectedFiles.length
          ? await chatApi.uploadAttachments(context, selectedFiles)
          : [];
        const attachments = cleanLink
          ? [{ url: cleanLink, name: "Посилання" }]
          : [];
        attachments.push(...uploadedAttachments);
        const socket = socketRef.current;
        if (socket?.connected) {
          await new Promise<void>((resolve, reject) => {
            socket.emit(
              "chat:message:send",
              {
                ...context,
                text: cleanText,
                attachments,
                replyToId: replyingTo?._id,
              },
              (response: any) => {
                if (response?.ok) {
                  if (response.message) onMessageSent?.(response.message);
                  resolve();
                }
                else reject(new Error(response?.message));
              }
            );
          });
        } else {
          const message = await chatApi.sendMessage(context, {
            text: cleanText,
            attachments,
            replyToId: replyingTo?._id,
          });
          setMessages((current) => mergeMessage(current, message));
          onMessageSent?.(message);
        }
      }
      setText("");
      setLinkUrl("");
      setSelectedFiles([]);
      setShowLink(false);
      setReplyingTo(null);
      setEditing(null);
      socketRef.current?.emit("chat:typing", {
        ...context,
        isTyping: false,
      });
      scrollToBottom();
    } catch (requestError: any) {
      setError(
        requestError.response?.data?.message ||
          requestError.message ||
          "Не вдалося надіслати повідомлення."
      );
    } finally {
      setSending(false);
    }
  };

  const remove = async (message: ChatMessage) => {
    if (!window.confirm("Видалити повідомлення?")) return;
    try {
      const deleted = await chatApi.deleteMessage(message._id);
      setMessages((current) => mergeMessage(current, deleted));
      setMenuId("");
    } catch (requestError: any) {
      setError(
        requestError.response?.data?.message ||
          "Не вдалося видалити повідомлення."
      );
    }
  };

  const startEdit = (message: ChatMessage) => {
    setEditing(message);
    setReplyingTo(null);
    setSelectedFiles([]);
    setText(message.text);
    setMenuId("");
  };

  if (!open) return null;
  const typingNames = Object.values(typingUsers);

  return (
    <div
      className={
        embedded
          ? "relative flex h-full min-h-0 w-full"
          : "fixed inset-0 z-[80]"
      }
    >
      {!embedded && (
        <button
          type="button"
          onClick={onClose}
          className="absolute inset-0 bg-black/30 backdrop-blur-[1px]"
          aria-label="Закрити чат"
        />
      )}
      <aside
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={
          embedded
            ? "relative flex h-full min-h-0 w-full flex-col overflow-hidden bg-[#fffdf2]"
            : "absolute inset-y-0 right-0 flex w-full flex-col bg-[#fffdf2] shadow-2xl sm:w-[440px]"
        }
      >
        {dragActive && !editing && (
          <div className="pointer-events-none absolute inset-3 z-30 grid place-items-center rounded-[24px] border-2 border-dashed border-black bg-[#fff8cf]/95 shadow-[6px_6px_0_#ffd400]">
            <div className="px-6 text-center">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl border-2 border-black bg-[#ffd400] text-black shadow-[3px_3px_0_#000]">
                <DownloadCloud size={26} />
              </div>
              <p className="mt-4 text-lg font-black text-black">
                Відпустіть файли тут
              </p>
              <p className="mt-1 text-sm font-semibold text-[#716A62]">
                Документи, PDF, фото або архіви до 25 МБ
              </p>
            </div>
          </div>
        )}
        <header className="flex min-h-[76px] items-center justify-between gap-3 border-b-2 border-black/10 bg-white px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            {embedded && onMobileBack && (
              <button
                type="button"
                onClick={onMobileBack}
                className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border-2 border-black bg-[#ffd400] text-black lg:hidden"
                aria-label="Назад до списку чатів"
              >
                <ArrowLeft size={22} />
              </button>
            )}
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border-2 border-black bg-[#ffd400] text-black">
              {recipientId ? <MessageCircle size={21} /> : <Users size={21} />}
            </div>
            <div className="min-w-0">
              <p className="truncate font-black text-black">
                {recipientName || "Чат потоку"}
              </p>
              <p className="truncate text-xs text-[#817A72]">
                {cohortName || "Учасники потоку"}
              </p>
            </div>
          </div>
          {!embedded && (
            <button
              type="button"
              onClick={onClose}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border-2 border-black bg-[#ffd400] text-black"
              aria-label="Закрити"
            >
              <X size={18} />
            </button>
          )}
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-4">
          {hasMore && (
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              className="mx-auto mb-4 flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 text-xs font-bold text-[#625C55] shadow-sm disabled:opacity-60"
            >
              {loadingMore ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <ArrowDown size={14} className="rotate-180" />
              )}
              Попередні повідомлення
            </button>
          )}

          {loading ? (
            <div className="flex h-full items-center justify-center gap-2 text-sm text-[#817A72]">
              <Loader2 size={18} className="animate-spin" />
              Завантажуємо чат...
            </div>
          ) : messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center px-8 text-center">
              <div className="grid h-16 w-16 place-items-center rounded-2xl border-2 border-black bg-[#ffd400] text-black shadow-[5px_5px_0_#000]">
                <MessageCircle size={28} />
              </div>
              <p className="mt-4 font-black text-black">
                Почніть розмову
              </p>
              <p className="mt-1 text-sm leading-6 text-[#817A72]">
                {recipientId
                  ? "Цей діалог бачите лише ви та співрозмовник."
                  : "Повідомлення побачать усі учасники потоку."}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((message) => {
                const author = authorOf(message);
                const own = String(author._id || "") === String(currentUser?._id);
                const reply = replyOf(message);
                const canDelete =
                  own ||
                  Boolean(currentUser?.isAdmin || currentUser?.role === "admin");

                return (
                  <div
                    key={message._id}
                    className={`group flex ${
                      own ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div className="relative max-w-[86%]">
                      {!message.deletedAt && (
                        <button
                          type="button"
                          onClick={() =>
                            setMenuId(menuId === message._id ? "" : message._id)
                          }
                          className={`absolute top-1 grid h-7 w-7 place-items-center rounded-lg border border-black/10 bg-white text-[#817A72] opacity-100 shadow-sm sm:opacity-0 sm:group-hover:opacity-100 ${
                            own ? "-left-9" : "-right-9"
                          }`}
                          aria-label="Дії"
                        >
                          <MoreHorizontal size={15} />
                        </button>
                      )}
                      {menuId === message._id && (
                        <div
                          className={`absolute top-9 z-10 w-40 overflow-hidden rounded-xl border-2 border-black bg-white py-1 shadow-xl ${
                            own ? "right-0" : "left-0"
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setReplyingTo(message);
                              setEditing(null);
                              setMenuId("");
                            }}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold hover:bg-[#fff8cf]"
                          >
                            <CornerUpLeft size={14} /> Відповісти
                          </button>
                          {own && (
                            <button
                              type="button"
                              onClick={() => startEdit(message)}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold hover:bg-[#fff8cf]"
                            >
                              <Edit3 size={14} /> Редагувати
                            </button>
                          )}
                          {canDelete && (
                            <button
                              type="button"
                              onClick={() => remove(message)}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold text-[#9D3D2E] hover:bg-[#FFF2EF]"
                            >
                              <Trash2 size={14} /> Видалити
                            </button>
                          )}
                        </div>
                      )}
                      <div
                        className={`rounded-2xl px-3.5 py-2.5 shadow-sm ${
                          message.deletedAt
                            ? "border border-dashed border-[#D8D2C9] bg-transparent text-[#918981]"
                            : own
                            ? "rounded-br-md bg-[#ffd400] text-black border-2 border-black shadow-[3px_3px_0_#000]"
                            : "rounded-bl-md bg-white text-[#3A3530]"
                        }`}
                      >
                        {!own && !message.deletedAt && (
                          <p className="mb-1 text-[11px] font-black text-[#8a6f00]">
                            {authorName(author)}
                            {author.role === "admin" ? " · Адміністратор" : ""}
                          </p>
                        )}
                        {reply && (
                          <div
                            className={`mb-2 rounded-lg border-l-2 px-2 py-1 text-xs ${
                              own
                                ? "border-white/70 bg-white/10 text-white/80"
                                : "border-black bg-[#fff8cf] text-[#716A62]"
                            }`}
                          >
                            <p className="font-bold">
                              {authorName(authorOf(reply))}
                            </p>
                            <p className="truncate">
                              {reply.deletedAt
                                ? "Повідомлення видалено"
                                : reply.text || "Вкладення"}
                            </p>
                          </div>
                        )}
                        {message.deletedAt ? (
                          <p className="text-sm italic">Повідомлення видалено</p>
                        ) : (
                          <>
                            {message.text && (
                              <p className="whitespace-pre-wrap break-words text-sm leading-5">
                                {message.text}
                              </p>
                            )}
                            {message.attachments?.map((attachment) => {
                              const image = isImageAttachment(
                                attachment.mimeType,
                                attachment.name
                              );

                              if (image) {
                                return (
                                  <button
                                    key={attachment.url}
                                    type="button"
                                    onClick={() =>
                                      setPreviewImage({
                                        url: imagePreviewUrl(attachment.url),
                                        name: attachment.name,
                                      })
                                    }
                                    className={`mt-2 block overflow-hidden rounded-xl border ${
                                      own
                                        ? "border-black/20 bg-black/10"
                                        : "border-black/10 bg-[#fff8cf]"
                                    }`}
                                    title={attachment.name}
                                  >
                                    <img
                                      src={imagePreviewUrl(attachment.url)}
                                      alt={attachment.name}
                                      loading="lazy"
                                      className="max-h-72 w-full min-w-[180px] max-w-[320px] object-cover"
                                    />
                                  </button>
                                );
                              }

                              return (
                                <a
                                  key={attachment.url}
                                  href={attachment.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className={`mt-2 flex items-center gap-2 overflow-hidden rounded-lg px-2.5 py-2 text-xs font-bold ${
                                    own
                                      ? "bg-white/15 text-white"
                                      : "bg-[#fff8cf] text-black"
                                  }`}
                                >
                                  <span
                                    className={`grid h-8 w-8 shrink-0 place-items-center rounded-md ${
                                      own ? "bg-white/10" : "bg-white"
                                    }`}
                                  >
                                    <FileText size={15} />
                                  </span>
                                  <span className="min-w-0">
                                    <span className="block truncate">
                                      {attachment.name}
                                    </span>
                                    {attachment.size ? (
                                      <span className="block text-[10px] font-semibold opacity-70">
                                        {formatFileSize(attachment.size)}
                                      </span>
                                    ) : null}
                                  </span>
                                </a>
                              );
                            })}
                          </>
                        )}
                      </div>
                      <p
                        className={`mt-1 px-1 text-[10px] text-[#9A938B] ${
                          own ? "text-right" : "text-left"
                        }`}
                      >
                        {new Date(message.createdAt).toLocaleTimeString(
                          "uk-UA",
                          { hour: "2-digit", minute: "2-digit" }
                        )}
                        {message.editedAt ? " · змінено" : ""}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        <footer className="border-t-2 border-black/10 bg-white p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:p-4">
          {error && (
            <div className="mb-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold text-red-700">
              {error}
            </div>
          )}
          {typingNames.length > 0 && (
            <p className="mb-2 px-1 text-xs text-[#817A72]">
              {typingNames.slice(0, 2).join(", ")} друкує...
            </p>
          )}
          {(replyingTo || editing) && (
            <div className="mb-2 flex items-center justify-between gap-3 rounded-xl border border-black/10 bg-[#fff8cf] px-3 py-2">
              <div className="min-w-0">
                <p className="text-[11px] font-black uppercase text-black">
                  {editing ? "Редагування" : "Відповідь"}
                </p>
                <p className="truncate text-xs text-[#625C55]">
                  {(editing || replyingTo)?.text || "Вкладення"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEditing(null);
                  setReplyingTo(null);
                  setSelectedFiles([]);
                  setText("");
                }}
                className="text-[#817A72]"
              >
                <X size={16} />
              </button>
            </div>
          )}
          {showLink && !editing && (
            <input
              type="url"
              value={linkUrl}
              onChange={(event) => setLinkUrl(event.target.value)}
              placeholder="https://... посилання на файл або матеріал"
              className="mb-2 h-10 w-full rounded-xl border-2 border-black/10 px-3 text-sm outline-none focus:border-black focus:ring-4 focus:ring-[#ffd400]/30"
            />
          )}
          {selectedFiles.length > 0 && !editing && (
            <div className="mb-2 space-y-2 rounded-2xl border border-black/10 bg-[#fffdf2] p-2">
              {selectedFiles.map((file, index) => (
                <div
                  key={`${file.name}-${file.size}-${index}`}
                  className="flex items-center gap-2 rounded-xl bg-white px-3 py-2"
                >
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#ffd400] text-black">
                    {file.type.startsWith("image/") ? (
                      <ImageIcon size={17} />
                    ) : (
                      <FileText size={17} />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-black text-black">
                      {file.name}
                    </p>
                    <p className="text-[10px] font-semibold text-[#817A72]">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSelectedFile(index)}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[#817A72] hover:bg-[#F7F4EF] hover:text-black"
                    aria-label="Прибрати файл"
                  >
                    <X size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <form onSubmit={submit} className="flex items-end gap-2">
            {!editing && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={CHAT_FILE_ACCEPT}
                  onChange={(event) => handleFilesSelected(event.target.files)}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl border ${
                    selectedFiles.length
                      ? "border-black bg-[#ffd400] text-black"
                      : "border-black/10 text-[#716A62]"
                  }`}
                  title="Додати файл"
                >
                  <Paperclip size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => setShowLink((current) => !current)}
                  className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl border ${
                    showLink
                      ? "border-black bg-[#ffd400] text-black"
                      : "border-black/10 text-[#716A62]"
                  }`}
                  title="Додати посилання"
                >
                  <Link2 size={18} />
                </button>
              </>
            )}
            <textarea
              rows={1}
              maxLength={2000}
              value={text}
              onChange={(event) => notifyTyping(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  event.currentTarget.form?.requestSubmit();
                }
              }}
              placeholder="Написати повідомлення..."
              className="max-h-32 min-h-[44px] min-w-0 flex-1 resize-none rounded-xl border-2 border-black/10 px-3 py-2.5 text-sm outline-none focus:border-black focus:ring-4 focus:ring-[#ffd400]/30"
            />
            <button
              disabled={
                sending ||
                (!text.trim() && !linkUrl.trim() && selectedFiles.length === 0)
              }
              className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border-2 border-black bg-[#ffd400] text-black shadow-[3px_3px_0_#000] disabled:opacity-40"
              aria-label="Надіслати"
            >
              {sending ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Send size={18} />
              )}
            </button>
          </form>
        </footer>
      </aside>
      {previewImage && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
          <button
            type="button"
            onClick={() => setPreviewImage(null)}
            className="absolute inset-0"
            aria-label="Закрити перегляд фото"
          />
          <div className="relative z-10 max-h-[92vh] max-w-[92vw]">
            <button
              type="button"
              onClick={() => setPreviewImage(null)}
              className="absolute right-3 top-3 z-20 grid h-11 w-11 place-items-center rounded-2xl border-2 border-black bg-[#ffd400] text-black shadow-[3px_3px_0_#000]"
              aria-label="Закрити"
            >
              <X size={20} />
            </button>
            <img
              src={previewImage.url}
              alt={previewImage.name}
              className="max-h-[92vh] max-w-[92vw] rounded-2xl object-contain shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatDrawer;
